import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, TextField, Typography, CircularProgress,
  InputAdornment, IconButton, Alert,
} from '@mui/material';
import { SmartToy, Visibility, VisibilityOff, Lock, Person } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { auth } from '../services/api';
import useStore from '../store/useStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setToken } = useStore();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [maintenanceOn, setMaintenanceOn] = useState(false);

  useEffect(() => {
    auth.maintenanceStatus().then(r => setMaintenanceOn(!!r.data?.enabled)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const credentials = {
      username: (form.username || formData.get('username') || '').trim(),
      password: form.password || formData.get('password') || '',
    };
    if (!credentials.username || !credentials.password) {
      toast.error('Username and password required');
      return;
    }
    setLoading(true);
    try {
      const { data } = await auth.login(credentials);
      if (data.success) {
        setToken(data.token);
        setUser(data.user);
        toast.success('Login successful!');
        if (data.user?.role === 'admin') {
          navigate('/dashboard');
        } else {
          navigate('/my-subscription');
        }
      }
    } catch {
      /* handled by interceptor */
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0a1929' }}>
      <Card sx={{ width: 400, boxShadow: '0 8px 40px rgba(0,0,0,0.4)', borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SmartToy sx={{ fontSize: 56, color: '#1976D2' }} />
            <Typography variant="h5" fontWeight={700} color="primary">AI Trading Platform</Typography>
            <Typography variant="body2" color="text.secondary">Autonomous Options Trading Bot</Typography>
          </Box>
          {maintenanceOn && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Platform is under maintenance. Only admin accounts can log in right now.
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Username or Email" variant="outlined" margin="normal"
              name="username"
              autoComplete="username"
              value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Password" type={showPass ? 'text' : 'password'} variant="outlined" margin="normal"
              name="password"
              autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(!showPass)} edge="end">
                    {showPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>,
              }}
            />
            <Button
              type="submit" fullWidth variant="contained" size="large"
              sx={{ mt: 3, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
